import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { paymentHoldAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AddUserModal from '../../components/AddUserModal';
import {
  ArrowLeft, Search, Users, Briefcase, UserCheck, UserX, Eye, Edit, Ban, 
  Loader2, ChevronRight, Mail, Phone, MapPin, Calendar, Shield, LogIn,
  Archive, ArchiveRestore, Trash2, UserPlus, CreditCard, Send
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
import { resolveProfilePhoto } from '../../utils/profilePhoto';
import AdminUsersFilterBar from '../../components/AdminUsersFilterBar';  // Iter-290b Sprint C
const AdminUsers = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, user: currentUser } = useAuth();
  const canImpersonate = !!(currentUser && (currentUser.can_impersonate || isSuperAdmin()));
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('filter') || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [profileStatusFilter, setProfileStatusFilter] = useState('all');
  // Iter-292h — Default sort = Newest Talent First. The user's
  // explicit policy: sort never persists across reloads / filter
  // clears / navigation unless saved as a named view. Saved views
  // override the default only while selected.
  const DEFAULT_SORT = 'joined_newest';
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [advFilters, setAdvFilters] = useState({});
  const [savedViewId, setSavedViewId] = useState(null);
  // Iter-257 — Show seed/QA test accounts in the table with a "Hidden From
  // Clients" badge so internal staff can audit them without the badge
  // leaking into client-facing surfaces.
  const [includeTest, setIncludeTest] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Iter-284b — Server-side aggregate counts. The list endpoint only returns
  // the current page (per_page=50), so we cannot derive role totals from it.
  const [roleStats, setRoleStats] = useState(null);  // null = still loading
  const [showAddUser, setShowAddUser] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // 2026-06 — Bulk selection + bulk actions (delete / inbox message / SMS)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkModal, setBulkModal] = useState(null); // 'message' | 'sms' | 'delete'
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkSms, setBulkSms] = useState('');

  const toggleSelect = (id) => setSelectedIds((curr) => {
    const next = new Set(curr);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const searchTimeout = React.useRef(null);
  // Iter-291 bugfix — track the latest fetchUsers call so stale responses
  // (A→Z arriving after Z→A, etc.) don't overwrite the current view.
  const fetchSeqRef = React.useRef(0);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter, statusFilter, page, includeTest, sortBy, advFilters]);

  // Refresh the aggregate role counts whenever the include_test toggle changes.
  // (The other filters narrow the list but the header cards always reflect the
  // unfiltered population so admins see overall coverage.)
  useEffect(() => {
    fetchRoleStats();
  }, [includeTest]);

  // Debounced server-side search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      fetchUsers();
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  const fetchRoleStats = async () => {
    try {
      const p = new URLSearchParams();
      if (includeTest) p.append('include_test', 'true');
      const res = await api.get(`/admin/users/stats?${p.toString()}`);
      setRoleStats(res.data || {});
    } catch (e) {
      // non-fatal — leave whatever stats we had
      console.error('[AdminUsers] role stats fetch failed', e);
    }
  };

  const fetchUsers = async () => {
    const mySeq = ++fetchSeqRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      params.append('page', page);
      params.append('per_page', 50);
      if (includeTest) params.append('include_test', 'true');

      // Iter-291 bugfix — Sprint C sort + advanced filters were being held in
      // React state but never forwarded to the backend, so the dropdown
      // appeared cosmetic. Wire them up explicitly here.
      if (sortBy) params.append('sort', sortBy);
      if (advFilters && typeof advFilters === 'object') {
        Object.entries(advFilters).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '' || v === false) return;
          if (Array.isArray(v)) {
            if (v.length === 0) return;
            params.append(k, v.join(','));
            return;
          }
          params.append(k, String(v));
        });
      }

      const res = await api.get(`/admin/users?${params.toString()}`);
      // Iter-291 bugfix — discard stale responses (a slow A→Z reply must
      // never overwrite a freshly-issued Z→A view).
      if (mySeq !== fetchSeqRef.current) return;
      setUsers(res.data.users || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalCount(res.data.total || 0);
    } catch (error) {
      if (mySeq !== fetchSeqRef.current) return;
      // Quiet inline error — no toast spam on route changes. The caller can retry.
      // Preserve the exact failure for the admin to copy into a bug report.
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('[AdminUsers] fetch failed', { status, detail, error });
      setLoadError({ status, detail });
      setUsers([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      if (mySeq === fetchSeqRef.current) setLoading(false);
    }
  };

  // Iter-292d — Export currently filtered+sorted users as a CSV. Server
  // streams up to 5000 rows; UI warns admin to refine filters if truncated.
  const [exporting, setExporting] = useState(false);
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (includeTest) params.append('include_test', 'true');
      if (sortBy) params.append('sort', sortBy);
      if (advFilters && typeof advFilters === 'object') {
        Object.entries(advFilters).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '' || v === false) return;
          if (Array.isArray(v)) {
            if (v.length === 0) return;
            params.append(k, v.join(','));
            return;
          }
          params.append(k, String(v));
        });
      }
      const res = await api.get(`/admin/users/export-csv?${params.toString()}`, {
        responseType: 'blob',
      });
      const truncated = res.headers?.['x-truncated'] === 'true';
      const total = res.headers?.['x-total-matches'];
      const returned = res.headers?.['x-returned-rows'];

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `ievntur_users_${ts}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (truncated) {
        toast.warning(
          `Exported the first ${returned} of ${total} matches. Refine your filters to export the rest.`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Exported ${returned} users to CSV`);
      }
    } catch (e) {
      console.error('[AdminUsers] export failed', e);
      toast.error('CSV export failed — please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleAction = async (action, userId) => {
    setProcessing(true);
    try {
      if (action === 'suspend') {
        await api.patch(`/admin/users/${userId}/suspend`);
        toast.success('User suspended');
      } else if (action === 'activate') {
        await api.patch(`/admin/users/${userId}/activate`);
        toast.success('User activated');
      } else if (action === 'archive') {
        await api.patch(`/admin/users/${userId}/archive`);
        toast.success('User archived');
      } else if (action === 'unarchive') {
        await api.patch(`/admin/users/${userId}/unarchive`);
        toast.success('User restored');
      } else if (action === 'delete') {
        const res = await api.delete(`/admin/users/${userId}`);
        if (res.data?.fallback === 'archived') {
          toast.warning('Hard delete blocked — user archived instead.');
        } else {
          toast.success('User permanently deleted');
        }
      } else if (action === 'payment_hold') {
        await paymentHoldAPI.adminApply(userId, {
          reason: (actionModal?.reason || '').trim() || 'Outstanding booking payment / unpaid 60% deposit',
          amount: actionModal?.amount ? Number(actionModal.amount) : null,
          deadline: actionModal?.deadline || null,
          notes: (actionModal?.notes || '').trim() || null,
        });
        toast.success('Account placed On Hold for Payment');
      } else if (action === 'release_payment_hold') {
        await paymentHoldAPI.adminRelease(userId, { notes: (actionModal?.notes || '').trim() || null });
        toast.success('Payment hold released — access restored');
      } else if (action === 'verify_ach') {
        await paymentHoldAPI.adminVerifyAch(userId, { notes: (actionModal?.notes || '').trim() || null });
        toast.success('ACH payment verified — access restored');
      } else if (action === 'approve_profile') {
        await api.patch(`/admin/users/${userId}/approve-profile`);
        toast.success('Profile approved by admin');
      } else if (action === 'convert-role') {
        // Iter-257 — Admin Change-Account-Type. The dialog's `newRole`
        // is read from `actionModal.newRole`. Server preserves all
        // history; force=true bypasses the active-bookings safety net.
        const newRole = actionModal?.newRole;
        const force = !!actionModal?.force;
        const res = await api.post(`/admin/users/${userId}/convert-role`, {
          new_role: newRole, confirm: true, force,
        });
        toast.success(res.data?.message || `Converted to ${newRole}`);
      } else if (action === 'impersonate') {
        const reason = (actionModal?.reason || '').trim();
        if (reason.length < 10) {
          toast.error('Please enter a reason (at least 10 characters).');
          setProcessing(false);
          return;
        }
        const res = await api.post(`/admin/impersonate/start`, {
          target_user_id: userId,
          reason,
        });
        // Iter-183 P0 — Impersonation must update BOTH token AND user
        // object atomically. Previously we only swapped the token; the
        // stale admin `user` object stayed in localStorage so when
        // AuthContext re-hydrated after the page reload, ProtectedRoute
        // saw role='admin' and rejected /client/* routes (kicked back
        // to /login). Now we mirror the login flow: write a partial
        // user object built from the impersonate response, and stash
        // the original admin user so we can restore it cleanly.
        localStorage.setItem('original_token', localStorage.getItem('token'));
        const originalUser = localStorage.getItem('user');
        if (originalUser) localStorage.setItem('original_user', originalUser);
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('impersonated_user_email', res.data.user_email || '');
        localStorage.setItem('impersonator_email', res.data.impersonator_email || '');
        localStorage.setItem('impersonation_reason', reason);
        localStorage.setItem('impersonation_started_at', res.data.started_at || '');
        // Build a partial user from the impersonate response so AuthContext
        // re-hydrates as the impersonated user, not the admin.
        const impersonatedUser = {
          id: res.data.user_id,
          email: res.data.user_email,
          role: res.data.user_role || res.data.role,
          admin_role: null,
          admin_permissions: {},
        };
        localStorage.setItem('user', JSON.stringify(impersonatedUser));
        window.location.href = res.data.redirect_url || '/';
        return;
      }
      setActionModal(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    } finally {
      setProcessing(false);
    }
  };

  // Server-side search handles filtering — use users directly
  const filteredUsers = users;

  const visibleSelectableIds = filteredUsers
    .filter((u) => ['talent', 'client', 'agency'].includes(u.role))
    .map((u) => u.id);
  const allVisibleSelected = visibleSelectableIds.length > 0
    && visibleSelectableIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((curr) => {
      const next = new Set(curr);
      if (allVisibleSelected) visibleSelectableIds.forEach((id) => next.delete(id));
      else visibleSelectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const runBulkMessage = async () => {
    if (!bulkTitle.trim()) { toast.error('Enter a title'); return; }
    setBulkBusy(true);
    try {
      const res = await api.post('/admin/users/bulk-message', {
        user_ids: Array.from(selectedIds), title: bulkTitle.trim(), body: bulkBody.trim() || null,
      });
      toast.success(`Message sent to ${res.data.sent} of ${res.data.requested} users' inboxes`);
      setBulkModal(null); setBulkTitle(''); setBulkBody(''); clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk message failed');
    } finally { setBulkBusy(false); }
  };

  const runBulkSms = async () => {
    if (!bulkSms.trim()) { toast.error('Enter a message'); return; }
    setBulkBusy(true);
    try {
      const res = await api.post('/admin/users/bulk-sms', {
        user_ids: Array.from(selectedIds), message: bulkSms.trim(),
      });
      const d = res.data;
      toast.success(`SMS: ${d.sent} sent, ${d.failed} failed, ${d.no_phone} without phone`);
      setBulkModal(null); setBulkSms(''); clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk SMS failed');
    } finally { setBulkBusy(false); }
  };

  const runBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const res = await api.post('/admin/users/bulk-delete', { user_ids: Array.from(selectedIds) });
      toast.success(res.data.message || 'Accounts deleted');
      setBulkModal(null); clearSelection(); fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };


  const getRoleBadge = (role) => {
    const config = {
      talent: { label: 'Talent', className: 'bg-cyan-100 text-cyan-800' },
      client: { label: 'Client', className: 'bg-green-100 text-green-800' },
      admin: { label: 'Admin', className: 'bg-purple-100 text-purple-800' },
      agency: { label: 'Agency', className: 'bg-orange-100 text-orange-800' },
    };
    const c = config[role] || { label: role, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    if (status === 'suspended') return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
    return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-users-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} className="shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Button>
            <h1 className="text-lg sm:text-xl font-bold truncate">User Management</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={() => navigate('/admin/talent-communications')}
              variant="outline"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              size="sm"
              data-testid="talent-communications-btn"
            >
              <Send className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Talent Communications</span>
            </Button>
            <Button
              onClick={() => setShowAddUser(true)}
              className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
              size="sm"
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add User</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Filters */}
        <Card className="mb-4 sm:mb-6">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="user-search-input"
                />
              </div>
              <div className="grid grid-cols-2 md:flex gap-2 md:gap-3">
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-full md:w-40" data-testid="role-filter">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="talent">Talent</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                    <SelectValue placeholder="Account Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={profileStatusFilter} onValueChange={setProfileStatusFilter}>
                  <SelectTrigger className="w-full md:w-44 col-span-2 md:col-span-1" data-testid="profile-status-filter">
                    <SelectValue placeholder="Profile Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Profiles</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="incomplete">Incomplete</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                </SelectContent>
              </Select>
              {/* Iter-257 — Toggle test/seed accounts for QA debugging. */}
              <label
                className="inline-flex items-center gap-2 text-sm text-gray-700 col-span-2 md:col-span-1 cursor-pointer select-none"
                data-testid="include-test-toggle"
              >
                <input
                  type="checkbox"
                  checked={includeTest}
                  onChange={(e) => { setPage(1); setIncludeTest(e.target.checked); }}
                  className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                Include hidden test accounts
              </label>
              </div>
            </div>
            {/* Iter-290b Sprint C — sort, saved views, advanced filters */}
            <div className="mt-3 pt-3 border-t border-slate-100">
              <AdminUsersFilterBar
                sort={sortBy}
                onSortChange={(v) => { setSortBy(v); setPage(1); }}
                advFilters={advFilters}
                onAdvFiltersChange={(v) => { setAdvFilters(v); setPage(1); }}
                savedViewId={savedViewId}
                onSavedViewChange={(id) => { setSavedViewId(id); setPage(1); }}
                currentRoleFilter={roleFilter}
                onExportCsv={handleExportCsv}
                exporting={exporting}
                defaultSort={DEFAULT_SORT}
                onResetToDefault={() => { setSortBy(DEFAULT_SORT); setPage(1); }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats + Total Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            Showing {filteredUsers.length} of <strong>{totalCount}</strong> users
            {searchQuery && <span className="text-cyan-600"> matching "{searchQuery}"</span>}
          </p>
        </div>

        {/* 2026-06 — Bulk selection toolbar */}
        {visibleSelectableIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-3 p-2.5 rounded-lg border bg-gray-50" data-testid="bulk-toolbar">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 accent-cyan-600"
                checked={allVisibleSelected}
                onChange={toggleSelectAll}
                data-testid="bulk-select-all"
              />
              Select all ({visibleSelectableIds.length})
            </label>
            {selectedIds.size > 0 && (
              <>
                <span className="text-sm text-gray-600" data-testid="bulk-selected-count">
                  {selectedIds.size} selected
                </span>
                <div className="flex items-center gap-2 ml-auto">
                  <Button size="sm" variant="outline" onClick={() => setBulkModal('message')} data-testid="bulk-message-btn">
                    <Mail className="w-4 h-4 mr-1" /> Message
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setBulkModal('sms')} data-testid="bulk-sms-btn">
                    <Phone className="w-4 h-4 mr-1" /> SMS
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setBulkModal('delete')} data-testid="bulk-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">Clear</Button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card className="bg-cyan-50 border-cyan-200" data-testid="stat-total">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-cyan-700">
                {roleStats === null
                  ? <span className="inline-block w-12 h-7 bg-cyan-100 rounded animate-pulse" />
                  : (roleStats.total || totalCount)}
              </p>
              <p className="text-sm text-cyan-600">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200" data-testid="stat-clients">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-700">
                {roleStats === null
                  ? <span className="inline-block w-12 h-7 bg-green-100 rounded animate-pulse" />
                  : (roleStats.client || 0)}
              </p>
              <p className="text-sm text-green-600">Clients</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200" data-testid="stat-admins">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">
                {roleStats === null
                  ? <span className="inline-block w-12 h-7 bg-purple-100 rounded animate-pulse" />
                  : (roleStats.admin || 0)}
              </p>
              <p className="text-sm text-purple-600">Admins</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200" data-testid="stat-agencies">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">
                {roleStats === null
                  ? <span className="inline-block w-12 h-7 bg-orange-100 rounded animate-pulse" />
                  : (roleStats.agency || 0)}
              </p>
              <p className="text-sm text-orange-600">Agencies</p>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        {loadError ? (
          <Card className="border-red-200 bg-red-50" data-testid="users-load-error">
            <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
              <div>
                <p className="text-sm font-semibold text-red-800">Couldn't load users</p>
                <p className="text-xs text-red-700 mt-0.5">
                  {loadError.status ? `HTTP ${loadError.status} · ` : ''}{loadError.detail}
                </p>
                <p className="text-[11px] text-red-600 mt-1">
                  Usually a transient network blip. Click Retry — if it keeps happening, copy this
                  message into a bug report.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchUsers} data-testid="users-load-error-retry" className="border-red-300 text-red-800 hover:bg-red-100">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              No users found matching your criteria
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map((user) => (
              <Card key={user.id} className="hover:shadow-md transition-shadow overflow-hidden" data-testid="user-card">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {['talent', 'client', 'agency'].includes(user.role) && (
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-4 accent-cyan-600 shrink-0"
                          checked={selectedIds.has(user.id)}
                          onChange={() => toggleSelect(user.id)}
                          data-testid={`user-select-${user.id}`}
                          aria-label="Select user"
                        />
                      )}
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                        {resolveProfilePhoto(user) ? (
                          <img
                            src={resolveProfilePhoto(user)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <Users className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 break-words">
                            {(user.role === 'client' || user.role === 'agency')
                              ? (user.company_name || user.email)
                              : (user.first_name && user.last_name
                                  ? `${user.first_name} ${user.last_name}`
                                  : user.company_name || user.email)}
                          </p>
                          {getRoleBadge(user.role)}
                          {getStatusBadge(user.status)}
                          {user.payment_hold && (
                            <Badge
                              className="bg-orange-100 text-orange-800 border border-orange-200"
                              data-testid={`payment-hold-badge-${user.id}`}
                              title="On Hold for Payment — restricted portal access until payment is verified"
                            >
                              On Hold for Payment
                            </Badge>
                          )}
                          {user.is_test && (
                            <Badge
                              className="bg-amber-50 text-amber-700 border border-amber-200"
                              data-testid={`hidden-from-clients-badge-${user.id}`}
                              title="This account is flagged as test/seed/demo data and is invisible to clients & talent."
                            >
                              Hidden From Clients
                            </Badge>
                          )}
                        </div>
                        {(user.role === 'client' || user.role === 'agency') && user.company_name && (user.first_name || user.last_name) && (
                          <p className="text-xs text-gray-500 truncate" data-testid="user-card-contact-line">
                            Contact: {`${user.first_name || ''} ${user.last_name || ''}`.trim()}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 break-all">{user.email}</p>
                        <div className="flex items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-1 flex-wrap">
                          {user.phone && (
                            <span className="flex items-center gap-1 break-all">
                              <Phone className="w-3 h-3 shrink-0" /> {user.phone}
                            </span>
                          )}
                          {user.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {user.city}, {user.state}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Joined {formatDateMonthDay(user.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:flex md:items-center gap-2 w-full md:w-auto shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full md:w-auto"
                        onClick={() => {
                          if (user.role === 'talent') navigate(`/admin/talent/${user.id}`);
                          else if (user.role === 'client') navigate(`/admin/client/${user.id}`);
                          else setSelectedUser(user);
                        }}
                        data-testid="view-user-button"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {canImpersonate && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full md:w-auto"
                          onClick={() => setActionModal({ user, action: 'impersonate', reason: '' })}
                          data-testid="impersonate-button"
                        >
                          <LogIn className="w-4 h-4 mr-1" />
                          Login As
                        </Button>
                      )}
                      {user.status === 'active' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full md:w-auto border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setActionModal({ user, action: 'suspend' })}
                          data-testid="suspend-button"
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          Suspend
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full md:w-auto border-green-200 text-green-600 hover:bg-green-50"
                          onClick={() => setActionModal({ user, action: 'activate' })}
                          data-testid="activate-button"
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Activate
                        </Button>
                      )}
                      {(user.role === 'client' || user.role === 'agency') && (
                        user.payment_hold ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full md:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setActionModal({ user, action: 'release_payment_hold', notes: '' })}
                            data-testid="release-payment-hold-button"
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            Release Payment Hold
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full md:w-auto border-orange-200 text-orange-700 hover:bg-orange-50"
                            onClick={() => setActionModal({ user, action: 'payment_hold', reason: '', amount: '', deadline: '', notes: '' })}
                            data-testid="payment-hold-button"
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            On Hold for Payment
                          </Button>
                        )
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full md:w-auto border-amber-200 text-amber-700 hover:bg-amber-50"
                        onClick={() => setActionModal({ user, action: 'archive' })}
                        data-testid="archive-button"
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        Archive
                      </Button>
                      {/* Iter-257 — Change Account Type (admin only, talent ↔ client ↔ agency). */}
                      {['talent', 'client', 'agency'].includes(user.role) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full md:w-auto border-purple-200 text-purple-700 hover:bg-purple-50"
                          onClick={() => setActionModal({ user, action: 'convert-role', newRole: user.role })}
                          data-testid={`convert-role-btn-${user.id}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Change Type
                        </Button>
                      )}
                      {isSuperAdmin() && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full md:w-auto col-span-2 md:col-span-1 border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => setActionModal({ user, action: 'delete' })}
                          data-testid="delete-button"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6" data-testid="pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>

      {/* Action Confirmation Modal */}
      {actionModal && (
        <Dialog open={!!actionModal} onOpenChange={() => setActionModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionModal.action === 'suspend' && 'Suspend User'}
                {actionModal.action === 'activate' && 'Activate User'}
                {actionModal.action === 'impersonate' && 'Login As User'}
                {actionModal.action === 'archive' && 'Archive User'}
                {actionModal.action === 'delete' && 'Delete User Permanently'}
                {actionModal.action === 'convert-role' && 'Change Account Type'}
                {actionModal.action === 'payment_hold' && 'Place On Hold for Payment'}
                {actionModal.action === 'release_payment_hold' && 'Release Payment Hold'}
                {actionModal.action === 'verify_ach' && 'Verify ACH Payment'}
              </DialogTitle>
              <DialogDescription>
                {actionModal.action === 'suspend' && (
                  <>Are you sure you want to suspend <strong>{actionModal.user.email}</strong>? They will not be able to access the platform.</>
                )}
                {actionModal.action === 'activate' && (
                  <>Are you sure you want to activate <strong>{actionModal.user.email}</strong>? They will regain full access to the platform.</>
                )}
                {actionModal.action === 'impersonate' && (
                  <>You will be logged in as <strong>{actionModal.user.email}</strong>. Every action is recorded (admin, reason, IP, user-agent, start/end, duration). Click "Exit Impersonation" in the banner to return to your admin account.</>
                )}
                {actionModal.action === 'archive' && (
                  <>Archive <strong>{actionModal.user.email}</strong>? They will be hidden from admin lists and unable to log in, but their data is preserved. You can restore them later.</>
                )}
                {actionModal.action === 'delete' && (
                  <><strong className="text-red-600">This action is permanent.</strong> All account data for <strong>{actionModal.user.email}</strong> will be deleted. If related records block deletion, the account will be archived instead.</>
                )}
                {actionModal.action === 'convert-role' && (
                  <>Convert <strong>{actionModal.user.email}</strong> from <strong>{actionModal.user.role}</strong> to a new account type. All profile data, certifications, photos, messages, and event history are <strong>preserved</strong>.</>
                )}
                {actionModal.action === 'payment_hold' && (
                  <>Restrict <strong>{actionModal.user.email}</strong> to the payment, Evie and support screens only. This is a <strong>financial</strong> hold — it is not a suspension. They keep all bookings and data and regain access the moment payment is verified or you release the hold.</>
                )}
                {actionModal.action === 'release_payment_hold' && (
                  <>Restore full portal access for <strong>{actionModal.user.email}</strong>? An audit record will be written.</>
                )}
                {actionModal.action === 'verify_ach' && (
                  <>Confirm that the ACH transfer for <strong>{actionModal.user.email}</strong> has cleared. This releases the hold and restores access.</>
                )}
              </DialogDescription>
            </DialogHeader>
            {actionModal.action === 'payment_hold' && (
              <div className="space-y-3 px-1 pb-1" data-testid="payment-hold-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason (shown to the account holder)</label>
                  <textarea
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none"
                    placeholder="e.g. Outstanding 60% deposit for the Oct 18 Seattle event"
                    value={actionModal.reason || ''}
                    onChange={(e) => setActionModal((m) => ({ ...m, reason: e.target.value }))}
                    data-testid="payment-hold-reason-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding amount ($)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={actionModal.amount || ''}
                      onChange={(e) => setActionModal((m) => ({ ...m, amount: e.target.value }))}
                      data-testid="payment-hold-amount-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment deadline</label>
                    <Input
                      type="date"
                      value={actionModal.deadline || ''}
                      onChange={(e) => setActionModal((m) => ({ ...m, deadline: e.target.value }))}
                      data-testid="payment-hold-deadline-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Internal notes (admin only)</label>
                  <textarea
                    rows={2}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none"
                    placeholder="Not shown to the client"
                    value={actionModal.notes || ''}
                    onChange={(e) => setActionModal((m) => ({ ...m, notes: e.target.value }))}
                    data-testid="payment-hold-notes-input"
                  />
                </div>
              </div>
            )}
            {(actionModal.action === 'release_payment_hold' || actionModal.action === 'verify_ach') && (
              <div className="space-y-2 px-1 pb-1">
                <label className="block text-sm font-medium text-gray-700">Note (optional, audit record)</label>
                <textarea
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none"
                  placeholder="e.g. Payment confirmed via Stripe on Oct 12"
                  value={actionModal.notes || ''}
                  onChange={(e) => setActionModal((m) => ({ ...m, notes: e.target.value }))}
                  data-testid="payment-hold-release-notes-input"
                />
              </div>
            )}
            {actionModal.action === 'impersonate' && (
              <div className="space-y-2 px-1 pb-1" data-testid="impersonate-reason-block">
                <label htmlFor="impersonate-reason" className="block text-sm font-medium text-gray-700">
                  Reason for impersonation <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="impersonate-reason"
                  rows={3}
                  autoFocus
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none"
                  placeholder="e.g. Investigating booking sync bug on Samantha's Sept 11 gig"
                  value={actionModal.reason || ''}
                  onChange={(e) => setActionModal((m) => ({ ...m, reason: e.target.value }))}
                  data-testid="impersonate-reason-input"
                  maxLength={2000}
                />
                <div className="flex items-center justify-between text-xs">
                  <span
                    className={`${(actionModal.reason || '').trim().length < 10 ? 'text-amber-700' : 'text-emerald-700'}`}
                    data-testid="impersonate-reason-counter"
                  >
                    {(actionModal.reason || '').trim().length < 10
                      ? `${10 - (actionModal.reason || '').trim().length} more character${10 - (actionModal.reason || '').trim().length === 1 ? '' : 's'} required (min 10)`
                      : `${(actionModal.reason || '').trim().length} characters — ready`}
                  </span>
                  <span className="text-gray-400">Recorded in audit log</span>
                </div>
              </div>
            )}
            {actionModal.action === 'convert-role' && (
              <div className="space-y-3 px-1 pb-1">
                <label className="block text-sm font-medium text-gray-700">New account type</label>
                <Select
                  value={actionModal.newRole}
                  onValueChange={(v) => setActionModal((m) => ({ ...m, newRole: v }))}
                >
                  <SelectTrigger data-testid="convert-role-select">
                    <SelectValue placeholder="Select new role" />
                  </SelectTrigger>
                  <SelectContent>
                    {['talent', 'client', 'agency']
                      .filter((r) => r !== actionModal.user.role)
                      .map((r) => (
                        <SelectItem key={r} value={r} data-testid={`convert-role-option-${r}`}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300"
                    checked={!!actionModal.force}
                    onChange={(e) => setActionModal((m) => ({ ...m, force: e.target.checked }))}
                    data-testid="convert-role-force-checkbox"
                  />
                  Force conversion even if user has active bookings or shifts
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionModal(null)} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={() => handleAction(actionModal.action, actionModal.user.id)}
                disabled={
                  processing
                  || (actionModal.action === 'convert-role' && (!actionModal.newRole || actionModal.newRole === actionModal.user.role))
                  || (actionModal.action === 'impersonate' && ((actionModal.reason || '').trim().length < 10))
                }
                className={
                  actionModal.action === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' :
                  actionModal.action === 'suspend' ? 'bg-red-600 hover:bg-red-700' :
                  actionModal.action === 'archive' ? 'bg-amber-600 hover:bg-amber-700 text-white' :
                  actionModal.action === 'convert-role' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                  actionModal.action === 'payment_hold' ? 'bg-orange-600 hover:bg-orange-700 text-white' :
                  (actionModal.action === 'release_payment_hold' || actionModal.action === 'verify_ach') ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''
                }
                data-testid="confirm-action-btn"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {actionModal.action === 'suspend' && 'Suspend'}
                {actionModal.action === 'activate' && 'Activate'}
                {actionModal.action === 'impersonate' && 'Login As User'}
                {actionModal.action === 'archive' && 'Archive'}
                {actionModal.action === 'delete' && 'Delete Permanently'}
                {actionModal.action === 'payment_hold' && 'Place On Hold'}
                {actionModal.action === 'release_payment_hold' && 'Release Hold'}
                {actionModal.action === 'verify_ach' && 'Verify & Release'}
                {actionModal.action === 'convert-role' && `Convert to ${(actionModal.newRole || '').charAt(0).toUpperCase() + (actionModal.newRole || '').slice(1)}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* 2026-06 — Bulk Inbox Message modal */}
      <Dialog open={bulkModal === 'message'} onOpenChange={(o) => !o && setBulkModal(null)}>
        <DialogContent data-testid="bulk-message-modal">
          <DialogHeader>
            <DialogTitle>Message {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Sends a direct message to each selected user's iEvntür inbox.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Subject / title"
              value={bulkTitle}
              onChange={(e) => setBulkTitle(e.target.value)}
              data-testid="bulk-message-title"
            />
            <textarea
              className="w-full min-h-[120px] rounded-md border border-gray-300 p-2 text-sm"
              placeholder="Message body (optional)"
              value={bulkBody}
              onChange={(e) => setBulkBody(e.target.value)}
              data-testid="bulk-message-body"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={runBulkMessage} disabled={bulkBusy} data-testid="bulk-message-send" className="bg-cyan-600 hover:bg-cyan-700 text-white">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-06 — Bulk SMS modal */}
      <Dialog open={bulkModal === 'sms'} onOpenChange={(o) => !o && setBulkModal(null)}>
        <DialogContent data-testid="bulk-sms-modal">
          <DialogHeader>
            <DialogTitle>Text {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Sends an SMS to the phone number on each selected account. Users without a phone are skipped.</DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full min-h-[120px] rounded-md border border-gray-300 p-2 text-sm"
            placeholder="SMS message"
            value={bulkSms}
            onChange={(e) => setBulkSms(e.target.value)}
            maxLength={480}
            data-testid="bulk-sms-body"
          />
          <p className="text-xs text-gray-400">{bulkSms.length}/480 characters</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={runBulkSms} disabled={bulkBusy} data-testid="bulk-sms-send" className="bg-cyan-600 hover:bg-cyan-700 text-white">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send SMS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-06 — Bulk Delete confirm modal */}
      <Dialog open={bulkModal === 'delete'} onOpenChange={(o) => !o && setBulkModal(null)}>
        <DialogContent data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle>Delete {selectedIds.size} account{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
            <DialogDescription>
              Real Talent/Client accounts are <strong>deactivated (reversible)</strong> — their bookings,
              payments, messages and history are preserved. Only accounts flagged as test data are permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkModal(null)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={runBulkDelete} disabled={bulkBusy} data-testid="bulk-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <AddUserModal
        open={showAddUser}
        onOpenChange={setShowAddUser}
        onCreated={() => { fetchUsers(); }}
      />
    </div>
  );
};

export default AdminUsers;
